/**
 * PipelineMap — pure helpers for the Impact page's canvas bubble chart.
 *
 * This module used to also export a default React component that rendered the
 * legacy `/ivt/use-cases` Pipeline Map page (retired alongside UseCasesPage
 * and UcCatalogTree). What remains: the drawing primitives + type shapes that
 * IndustryPipelineOverview consumes to build the redesigned Impact-page chart.
 *
 *   - Color maps: UC_COLORS (per-industry), PRIORITY_COLORS (per-UC),
 *     ACCT_COLORS (per-freshness).
 *   - X-axis modes: XAxis enum + X_AXIS_LABELS.
 *   - Bubble type discriminated union: IndustryBubble / UcBubble / AssetBubble
 *     / AccountBubble / GhostBubble.
 *   - Row-shape helpers that turn a wire-op response into the drawable
 *     Bubble shape: aggregateByIndustry, ucBubblesFromLive,
 *     assetBubblesFromPipeline, accountBubbles.
 *   - Canvas primitives: drawChart, hitBubbles, bubbleColor, bubbleLabel,
 *     BubbleLayout + AxisCfg shapes.
 *
 * Hit-detection order carries the CR-282340594 fix: bubbles are drawn largest-
 * to-smallest (small on top) and iterated smallest-to-largest for hit testing,
 * so a visually-topmost small bubble wins a click over an overlapping big one.
 */

import type {
  PipelineBubble,
} from '../../../client/IvtHubClient';

// 行业配色 —— 照搬 prototype app.html 的 UC_COLORS（总览级用）。
export const UC_COLORS: Record<string, string> = {
  AUTO: '#d97706',
  FSI: '#2563eb',
  MFG: '#059669',
  MEAD: '#7c3aed',
  GAME: '#dc2626',
  HCLS: '#0891b2',
  RCH: '#db2777',
  ECOM: '#ea580c',
};

// Priority 配色 —— 照搬 prototype PRIORITY_COLORS（UC 下钻级用）。
export const PRIORITY_COLORS: Record<string, string> = {
  strategic: '#d97706',
  recommended: '#2563eb',
  opportunistic: '#64748b',
};

// 账户活跃度配色 —— 照搬 prototype ACCT_COLORS（Account 视图用，按 freshness）。
export const ACCT_COLORS: Record<string, string> = {
  active: '#16a34a',
  cooling: '#d97706',
  stale: '#dc2626',
};

// X 轴 4 档 + 英文标签 —— 照搬 prototype xLabelMap。
export type XAxis = 'opp_norm' | 'arr_weighted' | 'activity' | 'arsenal';
export const X_AXIS_LABELS: Record<XAxis, string> = {
  arsenal: 'Arsenal',
  activity: 'Activity Density',
  opp_norm: 'Opp Normalized',
  arr_weighted: 'ARR Weighted',
};

// Lifecycle counts on every non-ghost Bubble — power the Impact page's
// Realized-vs-Potential axis (X = launchedCount + closedLostCount, Y =
// openCount) and the client-side Win Rate coloring (all four grains). Kept
// separate from the axis / coloring code so `PipelineMap.tsx` stays a pure
// data-shape module.
interface LifecycleCounts {
  launchedCount: number;
  closedLostCount: number;
  openCount: number;
}

// 行业级聚合后的气泡（roll up 自多个 (industry, UC) bubble）。
export interface IndustryBubble extends LifecycleCounts {
  kind: 'industry';
  industry: string;
  arsenalCount: number;
  oppCount: number;
  totalArr: number;
  launchedArr: number;
  ucCount: number;
  // Σ(max score per opp) ÷ Σ oppCount, 行业级加权聚合。首页 X 轴。
  influenceDensityOpp: number;
}

// UC 级气泡（UC 下钻视图，1:1 映射 GetUcPipelineLive 的一行 + 影响力字段）。
export interface UcBubble extends LifecycleCounts {
  kind: 'uc';
  industry: string;
  ucName: string;
  priority: string;
  arsenalCount: number;
  oppCount: number;
  totalArr: number;
  launchedArr: number;
  influenceDensity: number;
  influenceDensityOpp: number;
  influenceDensityArr: number;
  penetrationPct: number;
  ivtActs: number;
  strongActs: number;
}

// 账户级气泡（Account 视图，1:1 映射 GetPipelineBubbles(groupBy=ACCOUNT) 的一行）。
export interface AccountBubble extends LifecycleCounts {
  kind: 'account';
  accountName: string;
  oppCount: number;
  ucCount: number;
  totalArr: number;
  launchedArr: number;
  influenceDensity: number;
  influenceDensityOpp: number;
  influenceDensityArr: number;
  relevantActs: number;
  ivtActs: number;
  freshness: string;
  bestDaysSince: number | null;
}

// Ghost 气泡（UC 下钻视图）：New Asset 有、但 pipeline 没有的 UC。无 pipeline 数据
// （ARR/arsenal/opp 全 0），不按数据轴定位 —— 固定画在右下角堆叠，点击跳 New Assets。
// LifecycleCounts fields are always 0 (ghosts represent uncovered UCs with no
// pipeline attached), so no Realized-vs-Potential axis math applies here.
export interface GhostBubble extends LifecycleCounts {
  kind: 'ghost';
  industry: string;
  ucName: string;
  // 以下字段恒 0，仅为统一 Bubble 形状（drawChart/tooltip 读取）。
  arsenalCount: number;
  oppCount: number;
  totalArr: number;
  launchedArr: number;
}

// Asset-grain 气泡（Asset View）：每个 asset 一个 bubble。opps 通过 assets.sfdc_tag
// 关联到 opportunity_cache.tags。ucName 可选（asset 声明的 use_case，可能为空）。
// arsenalCount / priority 在 asset 粒度无意义，恒 0/null（保留占位以复用 tooltip 逻辑）。
export interface AssetBubble extends LifecycleCounts {
  kind: 'asset';
  industry: string;
  assetId: string;
  assetName: string;
  ucName?: string;
  arsenalCount: number;
  oppCount: number;
  totalArr: number;
  launchedArr: number;
  influenceDensity: number;
  influenceDensityOpp: number;
  influenceDensityArr: number;
  penetrationPct: number;
  ivtActs: number;
  strongActs: number;
}

export type Bubble = IndustryBubble | UcBubble | AccountBubble | GhostBubble | AssetBubble;

// 框选缩放的数据坐标窗口（null = 全图）。对齐 prototype _bubbleZoom。
interface ZoomWindow {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

// drawChart 把本次渲染的坐标系（绘图区 + 当前可视数据窗口）挂在 canvas 上，
// 供组件的拖拽框选把屏幕矩形换算回数据坐标（对齐 prototype canvas._zoomCoords）。
interface ZoomCoords {
  padLeft: number;
  padTop: number;
  pw: number;
  ph: number;
  viewMinX: number;
  viewMaxX: number;
  viewMinY: number;
  viewMaxY: number;
}
type CanvasWithCoords = HTMLCanvasElement & { _zoomCoords?: ZoomCoords };

// 当前视图的 X 轴配置（绘制 + tooltip 共用）。
export interface AxisCfg {
  valueOf: (b: Bubble) => number;
  title: string; // X 轴标题文字
  word: string; // 象限标签里的 X 维度词（Arsenal / Influence）
  isInfluence: boolean; // 影响刻度格式 + tooltip 影响力行
  xLabel: string; // tooltip 影响力行前缀
}

// 一次 draw pass 后产出的命中布局（不再 mutate bubble 对象 —— 见 prototype 移植 note）。
export interface BubbleLayout {
  bubble: Bubble;
  x: number;
  y: number;
  r: number;
}

export function bubbleLabel(b: Bubble): string {
  if (b.kind === 'industry') return b.industry;
  if (b.kind === 'account') return b.accountName;
  if (b.kind === 'asset') return b.assetName;
  return b.ucName;
}

// Ghost 气泡配色（虚线琥珀圈 + ★）—— 照搬 prototype。
const GHOST_COLOR = '#f59e0b';

// Asset 气泡默认色（Asset View 无 priority，按行业配色以区分）。
const ASSET_DEFAULT_COLOR = '#8b5cf6';

export function bubbleColor(b: Bubble): string {
  if (b.kind === 'industry') return UC_COLORS[b.industry] || '#6b7280';
  if (b.kind === 'account') return ACCT_COLORS[b.freshness] || '#64748b';
  if (b.kind === 'ghost') return GHOST_COLOR;
  if (b.kind === 'asset') return UC_COLORS[b.industry] || ASSET_DEFAULT_COLOR;
  return PRIORITY_COLORS[b.priority] || '#64748b';
}

/** 总览：把同行业的 (industry,UC) bubbles 聚合成一个行业气泡。
 *
 * influenceDensityOpp 是加权聚合口径：Σ(uc.influenceDensityOpp × uc.oppCount)
 * ÷ Σ oppCount。这样等价于"整个行业 Σ(max score per opp) ÷ Σ opps"，跟 Live
 * per-UC 口径一致（如果做无权平均，UC 少 opp 但密度极端的会被放大）。
 * 分母 = 0 时（行业没有 opp）返回 0。
 */
// Accepts any bubble shape carrying the industry-roll-up fields read below;
// structural typing keeps the helper usable from PipelineMap and from
// IndustryPipelineOverview. Keyed off `PipelineBubble` (GetPipelineBubbles), which is
// what both callers actually pass. It used to Pick off `UcPipelineBubble` — the output
// shape of the retired GetUcPipeline — which coupled this file to an op nothing calls
// and would have broken the build the moment the backend deleted it. PipelineBubble is
// a superset (verified field-by-field against the generated type), so the accepted set
// is unchanged.
export function aggregateByIndustry(
  bubbles: Array<Partial<Pick<PipelineBubble, 'ucName' | 'launchedCount'>>
    & Partial<Pick<PipelineBubble, 'closedLostCount' | 'openCount'>>
    & Pick<PipelineBubble,
      'industry' | 'arsenalCount' | 'oppCount' | 'totalArr'
      | 'launchedArr' | 'influenceDensityOpp'>>,
): IndustryBubble[] {
  // 过滤同 prototype：total_arr > 0 || arsenal_count > 0
  const filtered = bubbles.filter((b) => (b.totalArr ?? 0) > 0 || (b.arsenalCount ?? 0) > 0);
  const indMap: Record<string, IndustryBubble & { _inflSum: number }> = {};
  for (const b of filtered) {
    const ind = b.industry ?? '';
    if (!ind) continue;
    if (!indMap[ind]) {
      indMap[ind] = {
        kind: 'industry',
        industry: ind,
        arsenalCount: 0,
        oppCount: 0,
        totalArr: 0,
        launchedArr: 0,
        launchedCount: 0,
        closedLostCount: 0,
        openCount: 0,
        ucCount: 0,
        influenceDensityOpp: 0,
        _inflSum: 0,
      };
    }
    const agg = indMap[ind];
    const oppCount = b.oppCount ?? 0;
    const infl = b.influenceDensityOpp ?? 0;
    agg.arsenalCount += b.arsenalCount ?? 0;
    agg.oppCount += oppCount;
    agg.totalArr += b.totalArr ?? 0;
    agg.launchedArr += b.launchedArr ?? 0;
    agg.launchedCount += b.launchedCount ?? 0;
    agg.closedLostCount += b.closedLostCount ?? 0;
    agg.openCount += b.openCount ?? 0;
    agg.ucCount++;
    // Σ(max score per opp) 分子累加（influenceDensityOpp 是 max_score_sum / oppCount，
    // 反乘 oppCount 拿回分子；分母就是 Σ oppCount）。
    agg._inflSum += infl * oppCount;
  }
  return Object.values(indMap).map((agg) => {
    const { _inflSum, ...rest } = agg;
    return {
      ...rest,
      influenceDensityOpp: agg.oppCount > 0 ? _inflSum / agg.oppCount : 0,
    };
  });
}

/**
 * UC 下钻：per-UC bubble → UcBubble（带影响力字段）.
 *
 * Accepts any bubble carrying the UC-level influence fields. Keyed off
 * `PipelineBubble` (GetPipelineBubbles({industry})); it used to Pick off
 * `UcPipelineLiveBubble`, the output shape of the retired GetUcPipelineLive.
 */
export function ucBubblesFromLive(
  items: Array<
    Partial<Pick<PipelineBubble, 'ucName' | 'launchedCount'>>
    & Partial<Pick<PipelineBubble, 'closedLostCount' | 'openCount'>>
    & Pick<PipelineBubble,
      'priority' | 'arsenalCount' | 'oppCount' | 'totalArr'
      | 'launchedArr' | 'influenceDensity' | 'influenceDensityOpp'
      | 'influenceDensityArr' | 'penetrationPct' | 'ivtActs' | 'strongActs'
    >
  >,
  industry: string,
): UcBubble[] {
  return items
    .filter((b) => (b.totalArr ?? 0) > 0 || (b.arsenalCount ?? 0) > 0)
    .map((b) => ({
      kind: 'uc' as const,
      industry,
      ucName: b.ucName ?? '',
      priority: b.priority ?? 'opportunistic',
      arsenalCount: b.arsenalCount ?? 0,
      oppCount: b.oppCount ?? 0,
      totalArr: b.totalArr ?? 0,
      launchedArr: b.launchedArr ?? 0,
      launchedCount: b.launchedCount ?? 0,
      closedLostCount: b.closedLostCount ?? 0,
      openCount: b.openCount ?? 0,
      influenceDensity: b.influenceDensity ?? 0,
      influenceDensityOpp: b.influenceDensityOpp ?? 0,
      influenceDensityArr: b.influenceDensityArr ?? 0,
      penetrationPct: b.penetrationPct ?? 0,
      ivtActs: b.ivtActs ?? 0,
      strongActs: b.strongActs ?? 0,
    }));
}

/**
 * Asset 下钻：per-asset PipelineBubble → AssetBubble.
 *
 * Consumes the ASSET-grain rows from `GetPipelineBubbles({groupBy: 'asset'})`.
 * An asset with no matching opps (zero SFDC-tag hits) is filtered out —
 * ghost bubbles for empty assets are not rendered in Asset View (unlike UC
 * View where new-asset UCs get a placeholder dot).
 */
export function assetBubblesFromPipeline(
  items: Array<Pick<PipelineBubble,
    'arsenalCount' | 'oppCount' | 'totalArr' | 'launchedArr'
    | 'influenceDensity' | 'influenceDensityOpp' | 'influenceDensityArr'
    | 'penetrationPct' | 'ivtActs' | 'strongActs'>
    & Partial<Pick<PipelineBubble, 'launchedCount'>>
    & Partial<Pick<PipelineBubble, 'closedLostCount' | 'openCount'>>
    & {
    industry?: string;
    ucName?: string;
    assetId?: string;
    assetName?: string;
  }>,
): AssetBubble[] {
  return items
    .filter((b) => !!b.assetId && (b.totalArr ?? 0) > 0)
    .map((b) => ({
      kind: 'asset' as const,
      industry: b.industry ?? '',
      assetId: b.assetId!,
      assetName: b.assetName ?? b.assetId!,
      ucName: b.ucName ?? undefined,
      arsenalCount: b.arsenalCount ?? 0,
      oppCount: b.oppCount ?? 0,
      totalArr: b.totalArr ?? 0,
      launchedArr: b.launchedArr ?? 0,
      launchedCount: b.launchedCount ?? 0,
      closedLostCount: b.closedLostCount ?? 0,
      openCount: b.openCount ?? 0,
      influenceDensity: b.influenceDensity ?? 0,
      influenceDensityOpp: b.influenceDensityOpp ?? 0,
      influenceDensityArr: b.influenceDensityArr ?? 0,
      penetrationPct: b.penetrationPct ?? 0,
      ivtActs: b.ivtActs ?? 0,
      strongActs: b.strongActs ?? 0,
    }));
}

/**
 * Account 视图：GetPipelineBubbles(groupBy=ACCOUNT) 的一行 → AccountBubble.
 *
 * Accepts the ACCOUNT-grain rows from `GetPipelineBubbles({groupBy: 'account',
 * industry})`. Structural typing on the account-only fields (`accountName`,
 * `ucCount`, `freshness`, `bestDaysSince`) plus the shared metric fields —
 * matches the `PipelineBubble` shape without pinning the import here so this
 * helper also fits any narrower test fixture.
 */
type AccountGrainBubbleInput = Pick<PipelineBubble,
  'accountName' | 'ucCount' | 'freshness' | 'bestDaysSince'
  | 'oppCount' | 'totalArr' | 'influenceDensity'
  | 'influenceDensityOpp' | 'influenceDensityArr' | 'relevantActs' | 'ivtActs'
  | 'launchedArr' | 'launchedCount' | 'closedLostCount' | 'openCount'>;

export function accountBubbles(items: AccountGrainBubbleInput[]): AccountBubble[] {
  return items
    .filter((b) => (b.totalArr ?? 0) > 0)
    .map((b) => ({
      kind: 'account' as const,
      accountName: b.accountName ?? '',
      oppCount: b.oppCount ?? 0,
      ucCount: b.ucCount ?? 0,
      totalArr: b.totalArr ?? 0,
      launchedArr: b.launchedArr ?? 0,
      launchedCount: b.launchedCount ?? 0,
      closedLostCount: b.closedLostCount ?? 0,
      openCount: b.openCount ?? 0,
      influenceDensity: b.influenceDensity ?? 0,
      influenceDensityOpp: b.influenceDensityOpp ?? 0,
      influenceDensityArr: b.influenceDensityArr ?? 0,
      relevantActs: b.relevantActs ?? 0,
      ivtActs: b.ivtActs ?? 0,
      freshness: b.freshness ?? 'stale',
      bestDaysSince: b.bestDaysSince ?? null,
    }));
}

/**
 * Canvas 手绘气泡图。返回命中布局供 click/hover 检测（不 mutate bubble）。
 * isOverview 控制气泡最小半径/缩放/标签样式（对齐 prototype）。
 * axis 决定 X 取值/标题/刻度格式/tooltip 影响力行。
 */
export function drawChart(
  canvas: HTMLCanvasElement,
  bubbles: Bubble[],
  isOverview: boolean,
  axis: AxisCfg,
  zoom: ZoomWindow | null,
  tipEl: HTMLDivElement | null,
): BubbleLayout[] {
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, w, h);

  if (!bubbles.length) {
    ctx.fillStyle = '#64748b';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('No pipeline data', w / 2, h / 2);
    return [];
  }

  // Ghost 气泡不参与数据轴范围计算（无 pipeline 数据，固定画在右下角）。
  const ghosts = bubbles.filter((b): b is GhostBubble => b.kind === 'ghost');
  const dataBubbles = bubbles.filter((b) => b.kind !== 'ghost');

  const pad = { top: 20, right: 40, bottom: 40, left: 55 };
  const pw = w - pad.left - pad.right;
  const ph = h - pad.top - pad.bottom;
  // 数据轴满量程（用非 ghost 气泡算）。
  const fullMaxX =
    Math.max(...dataBubbles.map((b) => axis.valueOf(b)), axis.isInfluence ? 0.1 : 1) * 1.3;
  const fullMaxY = Math.max(...dataBubbles.map((b) => b.totalArr), 1) * 1.25;
  // 当前可视窗口（zoom 时收窄，否则 0→满量程）—— 对齐 prototype viewMin/Max。
  const viewMinX = zoom ? zoom.xMin : 0;
  const viewMaxX = zoom ? zoom.xMax : fullMaxX;
  const viewMinY = zoom ? zoom.yMin : 0;
  const viewMaxY = zoom ? zoom.yMax : fullMaxY;
  const spanX = viewMaxX - viewMinX || 1;
  const spanY = viewMaxY - viewMinY || 1;
  const maxR = Math.max(...dataBubbles.map((b) => b.oppCount), 1);
  // 半径缩放：总览气泡大（min 24, ×52），下钻气泡小（min 10, ×30）——对齐 prototype。
  const scaleR = (v: number) =>
    Math.max(isOverview ? 24 : 10, Math.sqrt(v / maxR) * (isOverview ? 52 : 30));

  // 坐标轴
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top);
  ctx.lineTo(pad.left, pad.top + ph);
  ctx.lineTo(pad.left + pw, pad.top + ph);
  ctx.stroke();

  // 网格 + X 刻度（影响力轴用 1 位小数，arsenal 轴用整数 —— 对齐 prototype）。
  ctx.font = '11px -apple-system, sans-serif';
  ctx.textAlign = 'center';
  for (let i = 0; i <= 4; i++) {
    const x = pad.left + (pw * i) / 4;
    ctx.beginPath();
    ctx.moveTo(x, pad.top);
    ctx.lineTo(x, pad.top + ph);
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 0.5;
    ctx.stroke();
    ctx.fillStyle = '#475569';
    const xtv = viewMinX + (spanX * i) / 4;
    ctx.fillText(axis.isInfluence ? xtv.toFixed(1) : String(Math.round(xtv)), x, pad.top + ph + 16);
  }
  // Y 刻度
  ctx.textAlign = 'right';
  for (let i = 0; i <= 4; i++) {
    const y = pad.top + ph - (ph * i) / 4;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + pw, y);
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 0.5;
    ctx.stroke();
    ctx.fillStyle = '#475569';
    ctx.fillText('$' + ((viewMinY + (spanY * i) / 4) / 1e6).toFixed(1) + 'M', pad.left - 6, y + 4);
  }

  // 轴标题
  ctx.fillStyle = '#64748b';
  ctx.font = 'bold 11px -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(axis.title, pad.left + pw / 2, h - 4);
  ctx.save();
  ctx.translate(13, pad.top + ph / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText('Pipeline ARR', 0, 0);
  ctx.restore();

  // 均值虚线（用非 ghost 气泡算 X 当前轴取值）
  const meanBase = dataBubbles.length ? dataBubbles : bubbles;
  const meanX = meanBase.reduce((s, b) => s + axis.valueOf(b), 0) / meanBase.length;
  const meanY = meanBase.reduce((s, b) => s + b.totalArr, 0) / meanBase.length;
  ctx.setLineDash([5, 4]);
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1;
  const mx = pad.left + ((meanX - viewMinX) / spanX) * pw;
  const my = pad.top + ph - ((meanY - viewMinY) / spanY) * ph;
  ctx.beginPath();
  ctx.moveTo(mx, pad.top);
  ctx.lineTo(mx, pad.top + ph);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(pad.left, my);
  ctx.lineTo(pad.left + pw, my);
  ctx.stroke();
  ctx.setLineDash([]);

  // 象限标签（X 维度词随轴变化）
  ctx.font = '10px -apple-system, sans-serif';
  ctx.fillStyle = '#94a3b8';
  ctx.textAlign = 'center';
  const qx1 = pad.left + pw * 0.22;
  const qx2 = pad.left + pw * 0.78;
  const qy1 = pad.top + 16;
  const qy2 = pad.top + ph - 8;
  ctx.fillText(`⬆ High ARR · Low ${axis.word}`, qx1, qy1);
  ctx.fillText(`⬆ High ARR · High ${axis.word} ★`, qx2, qy1);
  ctx.fillText(`Low ARR · Low ${axis.word}`, qx1, qy2);
  ctx.fillText(`Low ARR · High ${axis.word} →`, qx2, qy2);

  // 数据气泡（大→小排序，小气泡画在上层）。ghost 不在此列，单独固定画。
  const sorted = [...dataBubbles].sort((a, b) => b.oppCount - a.oppCount);
  const layout: BubbleLayout[] = [];
  for (const b of sorted) {
    const x = pad.left + ((axis.valueOf(b) - viewMinX) / spanX) * pw;
    const y = pad.top + ph - ((b.totalArr - viewMinY) / spanY) * ph;
    const r = scaleR(b.oppCount);
    // zoom 时窗口外的气泡跳过绘制（命中也排除，避免点到看不见的气泡）。
    if (zoom && (x < pad.left - r || x > pad.left + pw + r || y < pad.top - r || y > pad.top + ph + r)) {
      continue;
    }
    layout.push({ bubble: b, x, y, r });
    const color = bubbleColor(b);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = color + '25';
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.stroke();

    if (isOverview) {
      // 总览：行业名 + ARR + opps 三行标签居中
      ctx.fillStyle = color;
      ctx.font = 'bold 13px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText((b as IndustryBubble).industry, x, y - 4);
      ctx.fillStyle = '#334155';
      ctx.font = '11px -apple-system, sans-serif';
      ctx.fillText('$' + (b.totalArr / 1e6).toFixed(1) + 'M', x, y + 11);
      ctx.fillStyle = '#64748b';
      ctx.font = '10px -apple-system, sans-serif';
      ctx.fillText(b.oppCount + ' opps', x, y + 24);
    } else {
      // 下钻：UC/账户名标签（大气泡居中，小气泡右侧）——对齐 prototype。
      const name = bubbleLabel(b);
      const label = name.length > 22 ? name.slice(0, 20) + '..' : name;
      ctx.font = '10px -apple-system, sans-serif';
      if (r > 14) {
        ctx.fillStyle = '#1e293b';
        ctx.textAlign = 'center';
        ctx.fillText(label, x, y + 3);
      } else {
        ctx.fillStyle = '#475569';
        ctx.textAlign = 'left';
        ctx.fillText(label, x + r + 4, y + 4);
      }
    }
  }

  // Ghost 气泡：New Asset 有、pipeline 无的 UC。固定半径 10、右下角竖向堆叠，
  // 不随数据轴/zoom 变换 —— 照搬 prototype（虚线琥珀圈 + ★）。
  ctx.textAlign = 'center';
  ghosts.forEach((g, i) => {
    const r = 10;
    const x = pad.left + pw - 30;
    const y = pad.top + ph - 20 - i * 28;
    layout.push({ bubble: g, x, y, r });
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.setLineDash([3, 3]);
    ctx.fillStyle = 'rgba(245,158,11,0.08)';
    ctx.fill();
    ctx.strokeStyle = GHOST_COLOR;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = GHOST_COLOR;
    ctx.font = 'bold 10px -apple-system, sans-serif';
    ctx.fillText('★', x, y + 4);
  });

  // Hover tooltip —— 命中检测反向迭代（小气泡优先），tooltip 用 DOM+textContent
  // 而非 innerHTML（XSS sink，CR-282340594 / Aristotle 189：名称来自后端）。
  canvas.onmousemove = (e: MouseEvent) => {
    const rect = canvas.getBoundingClientRect();
    const ex = e.clientX - rect.left;
    const ey = e.clientY - rect.top;
    let hit: BubbleLayout | null = null;
    for (let i = layout.length - 1; i >= 0; i--) {
      const l = layout[i];
      if (Math.hypot(ex - l.x, ey - l.y) < l.r) {
        hit = l;
        break;
      }
    }
    if (!tipEl) return;
    if (!hit) {
      tipEl.style.display = 'none';
      canvas.style.cursor = 'default';
      return;
    }
    canvas.style.cursor = 'pointer';
    tipEl.style.display = 'block';
    tipEl.style.left = Math.min(hit.x + 15, w - 200) + 'px';
    tipEl.style.top = hit.y - 10 + 'px';
    tipEl.replaceChildren();
    const b = hit.bubble;
    // Ghost：独立的 New Asset tooltip（无 pipeline 数据）—— 照搬 prototype。
    if (b.kind === 'ghost') {
      const gTitle = document.createElement('div');
      gTitle.style.fontWeight = '700';
      gTitle.style.color = GHOST_COLOR;
      gTitle.textContent = `★ ${b.ucName}`;
      const gSub = document.createElement('div');
      gSub.style.marginTop = '3px';
      gSub.style.color = '#94a3b8';
      gSub.textContent = 'New Asset (无 Pipeline 数据)';
      const gHint = document.createElement('div');
      gHint.style.marginTop = '4px';
      gHint.style.fontSize = '10px';
      gHint.style.color = '#64748b';
      gHint.textContent = '点击跳转到 New Assets →';
      tipEl.append(gTitle, gSub, gHint);
      return;
    }
    const title = document.createElement('div');
    title.style.fontWeight = '700';
    title.style.color = bubbleColor(b);
    title.textContent = bubbleLabel(b);
    const arrLine = document.createElement('div');
    arrLine.style.marginTop = '3px';
    arrLine.textContent =
      b.kind === 'account'
        ? `ARR: $${(b.totalArr / 1e6).toFixed(1)}M`
        : `ARR: $${(b.totalArr / 1e6).toFixed(1)}M · Launched: $${(b.launchedArr / 1e6).toFixed(1)}M`;
    const statLine = document.createElement('div');
    statLine.textContent =
      b.kind === 'industry'
        ? `Opps: ${b.oppCount} · Arsenal: ${b.arsenalCount} · UCs: ${b.ucCount}`
        : b.kind === 'account'
          ? `Opps: ${b.oppCount} · UCs: ${b.ucCount} · ${b.freshness}`
          : b.kind === 'asset'
            ? `Opps: ${b.oppCount}${b.ucName ? ' · UC: ' + b.ucName : ''}`
            : `Opps: ${b.oppCount} · Arsenal: ${b.arsenalCount} · ${b.priority}`;
    tipEl.append(title, arrLine, statLine);
    // 影响力行（UC 下钻 influence 档 + Account 视图）——对齐 prototype tooltip。
    if (b.kind === 'uc' && axis.isInfluence) {
      const xVal = axis.valueOf(b);
      if (xVal > 0 || b.influenceDensity > 0) {
        const infLine = document.createElement('div');
        infLine.style.marginTop = '3px';
        infLine.style.color = '#7c3aed';
        infLine.textContent = `${axis.xLabel}: ${xVal.toFixed(2)} · Penetration: ${b.penetrationPct}% · IVT: ${b.ivtActs} acts`;
        tipEl.append(infLine);
      }
    } else if (b.kind === 'account') {
      const infLine = document.createElement('div');
      infLine.style.marginTop = '3px';
      infLine.style.color = '#7c3aed';
      infLine.textContent = `Influence: ${b.influenceDensityOpp.toFixed(2)} · IVT: ${b.ivtActs} acts`;
      tipEl.append(infLine);
    } else if (b.kind === 'asset' && b.influenceDensity > 0) {
      const infLine = document.createElement('div');
      infLine.style.marginTop = '3px';
      infLine.style.color = '#7c3aed';
      infLine.textContent = `Influence: ${b.influenceDensityOpp.toFixed(2)} · Penetration: ${b.penetrationPct}% · IVT: ${b.ivtActs} acts`;
      tipEl.append(infLine);
    }
  };
  canvas.onmouseleave = () => {
    if (tipEl) tipEl.style.display = 'none';
  };

  // 暴露本次坐标系给组件的拖拽框选（屏幕→数据换算）。
  (canvas as CanvasWithCoords)._zoomCoords = {
    padLeft: pad.left,
    padTop: pad.top,
    pw,
    ph,
    viewMinX,
    viewMaxX,
    viewMinY,
    viewMaxY,
  };

  return layout;
}

/** 命中点击位置的所有气泡（小→大，与绘制层叠一致）。 */
export function hitBubbles(layout: BubbleLayout[], ex: number, ey: number): Bubble[] {
  const hits: Bubble[] = [];
  for (let i = layout.length - 1; i >= 0; i--) {
    const l = layout[i];
    if (Math.hypot(ex - l.x, ey - l.y) < l.r + 4) hits.push(l.bubble);
  }
  return hits;
}

